import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { useState } from "react";
import type {
  Collection,
  DocumentType,
  FilterOptions,
  SearchFilters,
} from "../lib/documentSearch/types";
import { activeFilterCount } from "../lib/documentSearch/filters";
import { CollectionList } from "./CollectionList";
import { FilterGroup } from "./FilterGroup";

type SourceSidebarProps = {
  collections: Collection[];
  filters: SearchFilters;
  filterOptions: FilterOptions | null;
  onFiltersChange: (filters: SearchFilters) => void;
  onResetFilters: () => void;
  onCollectionBrowse: () => void;
};

export function SourceSidebar({
  collections,
  filters,
  filterOptions,
  onFiltersChange,
  onResetFilters,
  onCollectionBrowse,
}: SourceSidebarProps) {
  const count = activeFilterCount(filters);
  const [sourceSetsOpen, setSourceSetsOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);

  function handleCollectionToggle(collectionId: string) {
    const selected = filters.collections.includes(collectionId);
    const collectionsFilter = selected
      ? filters.collections.filter((id) => id !== collectionId)
      : [...filters.collections, collectionId];

    onFiltersChange({
      ...filters,
      collections: collectionsFilter,
    });
    if (collectionsFilter.length > 0) {
      onCollectionBrowse();
    }
  }

  return (
    <aside className="source-sidebar" aria-label="Collections and filters">
      <div className="source-sidebar__treasury">
        <img src="/brand/nt-logo.png" alt="National Treasury" />
      </div>

      <div className="panel-heading">
        <div>
          <p>Collections</p>
          <h2>Source sets</h2>
        </div>
        <div className="panel-heading__actions">
          {count > 0 ? <span className="count-badge">{count}</span> : null}
          <button
            className="small-icon-button"
            type="button"
            aria-controls="source-sets-section"
            aria-expanded={sourceSetsOpen}
            title={sourceSetsOpen ? "Collapse source sets" : "Expand source sets"}
            onClick={() => setSourceSetsOpen((current) => !current)}
          >
            {sourceSetsOpen ? (
              <ChevronDown size={16} aria-hidden="true" />
            ) : (
              <ChevronRight size={16} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
      <div id="source-sets-section" hidden={!sourceSetsOpen}>
        <CollectionList
          collections={collections}
          selectedIds={filters.collections}
          onToggle={handleCollectionToggle}
        />
      </div>

      <div className="sidebar-divider" />

      <div className="panel-heading panel-heading--inline">
        <div>
          <p>Refine</p>
          <h2>Filters</h2>
        </div>
        <div className="panel-heading__actions">
          <button className="text-button" type="button" onClick={onResetFilters}>
            <RotateCcw size={15} aria-hidden="true" />
            Reset
          </button>
          <button
            className="small-icon-button"
            type="button"
            aria-controls="filters-section"
            aria-expanded={filtersOpen}
            title={filtersOpen ? "Collapse filters" : "Expand filters"}
            onClick={() => setFiltersOpen((current) => !current)}
          >
            {filtersOpen ? (
              <ChevronDown size={16} aria-hidden="true" />
            ) : (
              <ChevronRight size={16} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {filterOptions ? (
        <div className="filter-stack" id="filters-section" hidden={!filtersOpen}>
          <FilterGroup<DocumentType>
            title="Document type"
            options={filterOptions.documentTypes}
            selected={filters.documentTypes}
            onToggle={(documentType) =>
              onFiltersChange({
                ...filters,
                documentTypes: filters.documentTypes.includes(documentType)
                  ? filters.documentTypes.filter((item) => item !== documentType)
                  : [...filters.documentTypes, documentType],
              })
            }
          />
          <FilterGroup<number>
            title="Year"
            options={filterOptions.years}
            selected={filters.years}
            onToggle={(year) =>
              onFiltersChange({
                ...filters,
                years: filters.years.includes(year)
                  ? filters.years.filter((item) => item !== year)
                  : [...filters.years, year],
              })
            }
          />
          <FilterGroup<string>
            title="Topic"
            options={filterOptions.tags}
            selected={filters.tags}
            onToggle={(tag) =>
              onFiltersChange({
                ...filters,
                tags: filters.tags.includes(tag)
                  ? filters.tags.filter((item) => item !== tag)
                  : [...filters.tags, tag],
              })
            }
          />
        </div>
      ) : null}
    </aside>
  );
}
